import {LinkText, Icon, Tag} from '@/components/atoms'
import {growthBookFeatureFlags} from '@/utils/constants/growthBookFeatureFlags'
import {useFeatureValue} from '@growthbook/growthbook-react'
import {DateTime} from 'luxon'
import {TAG_COLOR} from '@/utils/constants/colors'
import {ROUTES} from '@/utils/constants/routesConstants'

/**
 * Constants for PromotionList component
 */

/**
 * Column definitions for the sub-table (failed records)
 */
export const SUB_COLUMNS = [
  {
    columnKey: 'promotionName',
    header: 'PROMOTION NAME',
  },
  {
    columnKey: 'errorMessage',
    header: 'STATUS (REMARK)',
  },
]

/**
 * Column definitions for the main upload history table
 */
export const MAIN_COLUMNS = (downloadPromotionFile, downloadUploadError) => [
  {
    columnKey: 'fileName',
    header: 'FILE PATH',
    cellData: ({row: {original}}) => (
      <div className="w-[200px] break-words whitespace-normal flex flex-col" title={`${original?.storagePath}${original?.fileName}`}>
        <LinkText
            text={`${original?.storagePath}${original?.fileName}`}
            id={`${original?.storagePath}${original?.fileName}`}
            allowWrap={true}
            handleClick={event => {
              event.stopPropagation()
              downloadPromotionFile(original?.id)
            }}
          />
      </div>
    ),
  },
  {
    columnKey: 'uploadedAt',
    header: 'IMPORT DATE',
    cellData: ({row: {original}}) => {
      const formatted = ToFormattedDateTime(original?.uploadedAt)
      return <div>{formatted ?? '—'}</div>
    },
  },
  {
    columnKey: 'processingStartedAt',
    header: 'JOB START DATE',
    cellData: ({row: {original}}) => {
      const formatted = ToFormattedDateTime(original?.processingStartedAt)
      return <div>{formatted ?? '—'}</div>
    },
  },
  {
    columnKey: 'processingCompletedAt',
    header: 'JOB END DATE',
    cellData: ({row: {original}}) => {
      const formatted = ToFormattedDateTime(original?.processingCompletedAt)
      return <div>{formatted ?? '—'}</div>
    },
  },
  {
    columnKey: 'statistics',
    header: 'STATISTICS',
    cellData: ({row: {original}}) => (
      <div className="flex flex-col">
        {Number(original?.successfulRecords) > 0 && <div>{original?.successfulRecords} Records</div>}
        {Number(original?.failedRecords) > 0 && (
          <LinkText
            text={`${original?.failedRecords} Failed`}
            id={original?.failedRecords + ' ' + original?.fileUploadUUID}
            fontColor="#E46666"
            fontWeight="bold"
            handleClick={event => {
              event.stopPropagation()
              downloadUploadError(original?.id)
            }}
          />
        )}
        {Number(original?.successfulRecords) === 0 && Number(original?.failedRecords) === 0 && (<div>{'—'}</div>)}
      </div>
    ),
  },
  {
    columnKey: 'createdBy',
    header: 'UPLOADED BY',
    cellData: ({row: {original}}) => {
      return <div>{original?.uploadedBy ?? 'Unknown'}</div>
    },
  },
]

/**
 * Cell data mapper object for DynamicColumnTable
 */


export const cellDataMapperObj = (navigate, handleCopyClick) => ({
  name: ({row: {original} = {}}) => (
    <LinkText
      text={original?.name}
      id={original?.id}
      handleClick={() => {
        const promotionId = original?.id
        navigate(ROUTES.PROMOTION_DETAIL.replace(':id', promotionId))
      }}
    />
  ),
  promotionType: ({row: {original} = {}}) => original?.promotionType,
  noOfStores: ({row: {original} = {}}) => (original?.noOfStores === null ? '-' : original?.noOfStores),
  percentPromotionAccepted: ({row: {original} = {}}) => {
    const percentage = original?.percentagePromotionAccepted
    const showTag = typeof percentage === 'number' && percentage < 90

    if (percentage === null) return '-'
    if (showTag) {
      return <Tag text={`${percentage}%`} color={TAG_COLOR.RED_600} />
    }
    return `${percentage}%`
  },
  displayOrder: ({row: {original} = {}}) => original?.displayOrder,
  activeStartDate: ({row: {original} = {}}) => {
    const dateString = original?.activeStartDate
    if (!dateString) return ''
    const date = new Date(dateString)
    const year = date.getFullYear()
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const day = date.getDate().toString().padStart(2, '0')
    return `${year}-${month}-${day}`
  },
  activeEndDate: ({row: {original} = {}}) => {
    const dateString = original?.activeEndDate
    if (!dateString) return ''
    const date = new Date(dateString)
    const year = date.getFullYear()
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const day = date.getDate().toString().padStart(2, '0')
    return `${year}-${month}-${day}`
  },
  status: ({row: {original} = {}}) => {
    let {status, id, promotionType} = original
    status = status?.[0] + status?.slice(1).toLocaleLowerCase()
    return (
      <div className="flex flex-row items-center justify-end gap-2">
        <p>{status}</p>
        <button className="cursor-pointer" onClick={() => handleCopyClick(id, promotionType)} title="Duplicate">
          <Icon
            icon="content_copy"
            size="medium"
            className="!ml-[40px] flex h-10 w-10 items-center justify-center rounded-sm border border-[#4e4e4e] !text-[#0268d7]"
          />
        </button>
      </div>
    )
  },
})


// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Convert an ISO/UTC string to Mexico-City time string (handles DST)
 */

const MX_ZONE = 'America/Mexico_City'
const DATE_FMT = 'yyyy-MM-dd HH:mm:ss'
const ToFormattedDateTime = iso => {
  const timeZone = useFeatureValue(growthBookFeatureFlags.AdminTimezone)
  return iso
    ? DateTime.fromISO(iso, {zone: 'utc'})
        .setZone(timeZone ? timeZone : MX_ZONE)
        .toFormat(DATE_FMT)
    : null
}
